import { ComposerPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { IconArrowUp, IconSquare } from '@tabler/icons-react';

import { TooltipIconButton } from '../tooltip-icon-button/TooltipIconButton.tsx';
import classes from './ComposerAction.module.css';
import { useHasPendingToolCall } from '#plugin-chat/hooks/useHasPendingToolCall.ts';

/**
 * Visual styling shared by every action button rendered in the composer.
 * The three buttons (Send, cancel-and-send, Stop) are otherwise gated by
 * different runtime predicates but must look identical — extract to one
 * place so the look stays in lockstep.
 */
const ACTION_BUTTON_STYLE = {
  color: 'white',
  position: 'left',
  size: 'sm',
  variant: 'transparent',
} as const;

/**
 * The arrow-up "Send message" icon button — used in two gates: as a
 * child of `ComposerPrimitive.Send` (`type='submit'`, no `onClick` —
 * the primitive merges its own submit behavior via `asChild`) AND as
 * a standalone "cancel-and-send" button during a pending interrupt
 * (`type='button'`, explicit `onClick` to bypass the primitive's
 * isRunning gate). One component so the look and labels stay in sync.
 */
function SendIconButton({ type, onClick }: { type: 'submit' | 'button'; onClick?: () => void }) {
  return (
    <TooltipIconButton
      aria-label="Send message"
      {...ACTION_BUTTON_STYLE}
      onClick={onClick}
      tooltip="Send message"
      type={type}
    >
      <IconArrowUp />
    </TooltipIconButton>
  );
}

/**
 * Renders the context-sensitive action button for the chat composer.
 *
 * Three mutually exclusive states drive a single button:
 *
 * - **Idle** (`!isRunning`) → `ComposerPrimitive.Send`. The primitive
 *   handles its own empty/disabled gating.
 * - **Running with a pending tool call AND text in the composer** →
 *   custom cancel-and-send button (`aui.composer().send()`).
 *   `wrappedChat.sendMessage` cancels the pending interrupt before
 *   starting the new turn — same SSOT as the Enter handler in
 *   `Composer.tsx`. Mouse parity with that keyboard path.
 * - **Running otherwise** (streaming, OR pending tool with an empty
 *   composer) → `ComposerPrimitive.Cancel`. It routes through
 *   `wrappedChat.stop` → `cancelInterrupts`, the same path ESC takes,
 *   so a visible Stop button can abort a pending `askUserQuestion`
 *   too (not only ordinary streaming).
 *
 * The pending-tool + empty-composer case shows Stop rather than a
 * disabled Send: the user clearly wants out, the only meaningful
 * action is to abort.
 */
export function ComposerAction() {
  const aui = useAui();
  const hasPendingToolCall = useHasPendingToolCall();
  const composerIsEmpty = useAuiState((s) => s.composer.isEmpty);
  const isRunning = useAuiState(({ thread }) => thread.isRunning);

  const showCancelAndSend = isRunning && hasPendingToolCall && !composerIsEmpty;
  const showStop = isRunning && !showCancelAndSend;
  const showSend = !isRunning;

  return (
    <div className={classes.composerActions}>
      {showSend && (
        // asChild merges Send primitive behavior with SendIconButton without wrapper div.
        <ComposerPrimitive.Send asChild>
          <SendIconButton type="submit" />
        </ComposerPrimitive.Send>
      )}

      {showCancelAndSend && <SendIconButton onClick={() => aui.composer().send()} type="button" />}

      {showStop && (
        <ComposerPrimitive.Cancel asChild>
          <TooltipIconButton
            aria-label="Stop generating"
            {...ACTION_BUTTON_STYLE}
            tooltip="Stop generating"
            type="submit"
          >
            <IconSquare />
          </TooltipIconButton>
        </ComposerPrimitive.Cancel>
      )}
    </div>
  );
}
